/**
 * Fichier : chat_widget/src/widget/hooks/useVehicleActions.ts
 *
 * Principe :
 * Ce hook isole les actions spécifiques liées aux véhicules dans le widget de chat.
 *
 * Il est responsable de :
 * - récupérer les détails d’un véhicule via l’API `/chat`
 * - gérer l’état du véhicule sélectionné (`selectedCar`)
 * - exposer un état de chargement dédié (`loadingVehicle`)
 * - permettre le reset du véhicule sélectionné (`clearSelectedCar`)
 *
 * Objectif architectural :
 * Séparer les actions "métier véhicule" de la logique conversationnelle
 * afin de garder `useChatPanel` centré sur l’orchestration globale.
 */
import { useCallback, useRef, useState } from "react";
import { API_CHAT_URL } from "../../config";
import type { Vehicle } from "../types/VehicleTypes";

export function useVehicleActions({
  sessionId,
  userId,
}: {
  sessionId: string;
  userId: string | null;
}) {
  const [selectedCar, setSelectedCar] = useState<Vehicle | null>(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);

  const vehicleAbortRef = useRef<AbortController | null>(null);

  const getVehicleDetails = useCallback(
    async (carId: string) => {
      if (!carId || loadingVehicle) return;
      if (!userId) return;
      // Abort any previous in-flight vehicle request
      if (vehicleAbortRef.current) {
        vehicleAbortRef.current.abort();
      }
      const controller = new AbortController();
      vehicleAbortRef.current = controller;

      setLoadingVehicle(true);
      setSelectedCar(null);

      try {
        const res = await fetch(`${API_CHAT_URL}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify({
            sessionId,
            message: null,
            carId,
            userId,
          }),
        });

        if (!res.ok) {
          const bodyText = await res.text().catch(() => "");
          throw new Error(`API error ${res.status} ${bodyText}`.trim());
        }

        const data = (await res.json()) as {
          meta?: { vehicle?: Vehicle };
        };

        if (data?.meta?.vehicle) {
          setSelectedCar(data.meta.vehicle);
        }
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        console.error("Erreur getVehicleDetails:", e);
      } finally {
        // Clear controller only if it is still the active one
        if (vehicleAbortRef.current === controller) {
          vehicleAbortRef.current = null;
        }
        setLoadingVehicle(false);
      }
    },
    [loadingVehicle, sessionId, userId],
  );

  const clearSelectedCar = useCallback(() => {
    setSelectedCar(null);
  }, []);

  const cancelVehicleRequests = useCallback(() => {
    if (vehicleAbortRef.current) {
      vehicleAbortRef.current.abort();
      vehicleAbortRef.current = null;
    }
    setLoadingVehicle(false);
  }, []);

  return {
    selectedCar,
    setSelectedCar,
    clearSelectedCar,
    getVehicleDetails,
    loadingVehicle,
    cancelVehicleRequests,
  };
}
